/**
 * Data Sanitization and Masking Utility
 * Provides comprehensive data protection for logging
 */

// Sensitive field names that should be completely removed
const SENSITIVE_FIELDS = [
  'password', 'passwd', 'pwd', 'pass',
  'secret', 'token', 'apiKey', 'api_key', 'key',
  'accessToken', 'refreshToken', 'authToken', 'auth_token',
  'creditCard', 'cardNumber', 'card_number', 'cvv', 'cvc', 'ccv',
  'ssn', 'socialSecurity', 'social_security_number',
  'bankAccount', 'accountNumber', 'account_number',
  'authorization', 'auth', 'bearer',
  'privateKey', 'private_key', 'secretKey', 'secret_key'
];

// Sensitive patterns that should be masked
const SENSITIVE_PATTERNS = [
  // Credit card numbers (various formats)
  { pattern: /\b\d{4}[-\s]?\d{4}[-\s]?\d{4}[-\s]?\d{4}\b/g, replacement: '****-****-****-****' },
  { pattern: /\b\d{13,19}\b/g, replacement: '[MASKED_CARD]' },
  
  // Email addresses (partial masking)
  { pattern: /\b([a-zA-Z0-9._%+-]+)@([a-zA-Z0-9.-]+\.[a-zA-Z]{2,})\b/g, replacement: (match, user, domain) => {
    const maskedUser = user.length > 2 ? user.substring(0, 2) + '***' : '***';
    return `${maskedUser}@${domain}`;
  }},
  
  // Phone numbers
  { pattern: /\b(\+?1[-.\s]?)?\(?([0-9]{3})\)?[-.\s]?([0-9]{3})[-.\s]?([0-9]{4})\b/g, replacement: '***-***-$4' },
  
  // SSN
  { pattern: /\b\d{3}-?\d{2}-?\d{4}\b/g, replacement: '***-**-****' },
  
  // JWT tokens
  { pattern: /\beyJ[a-zA-Z0-9_-]*\.[a-zA-Z0-9_-]*\.[a-zA-Z0-9_-]*\b/g, replacement: 'eyJ***.[MASKED_JWT]' },
  
  // API keys (common patterns)
  { pattern: /\b(sk_test_|sk_live_|pk_test_|pk_live_)[a-zA-Z0-9]{20,}\b/g, replacement: '$1********' },
  { pattern: /\b[a-zA-Z0-9]{32,}\b/g, replacement: '[MASKED_API_KEY]' }
];

/**
 * Filter out sensitive fields from an object
 * LAYER 1: Prevention - Remove sensitive fields entirely
 */
export const filterSensitiveFields = (obj, depth = 0) => {
  if (depth > 10) return '[MAX_DEPTH_REACHED]'; // Prevent infinite recursion
  
  if (!obj || typeof obj !== 'object') return obj;
  
  if (Array.isArray(obj)) {
    return obj.map(item => filterSensitiveFields(item, depth + 1));
  }
  
  const filtered = {};
  
  for (const [key, value] of Object.entries(obj)) {
    const lowerKey = key.toLowerCase();
    
    // Check if this field should be filtered out
    if (SENSITIVE_FIELDS.some(field => lowerKey.includes(field))) {
      filtered[key] = '[REDACTED - Sensitive field not logged]';
    } else if (value && typeof value === 'object') {
      // Recursively filter nested objects
      filtered[key] = filterSensitiveFields(value, depth + 1);
    } else {
      filtered[key] = value;
    }
  }
  
  return filtered;
};

/**
 * Mask sensitive patterns in strings
 * LAYER 2: Fail-safe - Mask sensitive patterns that slip through
 */
export const maskSensitivePatterns = (obj, depth = 0) => {
  if (depth > 10) return '[MAX_DEPTH_REACHED]'; // Prevent infinite recursion
  
  if (!obj) return obj;
  
  if (typeof obj === 'string') {
    let masked = obj;
    
    // Apply all sensitive patterns
    SENSITIVE_PATTERNS.forEach(({ pattern, replacement }) => {
      if (typeof replacement === 'function') {
        masked = masked.replace(pattern, replacement);
      } else {
        masked = masked.replace(pattern, replacement);
      }
    });
    
    return masked;
  }
  
  if (Array.isArray(obj)) {
    return obj.map(item => maskSensitivePatterns(item, depth + 1));
  }
  
  if (typeof obj === 'object') {
    const masked = {};
    
    for (const [key, value] of Object.entries(obj)) {
      masked[key] = maskSensitivePatterns(value, depth + 1);
    }
    
    return masked;
  }
  
  return obj;
};

/**
 * Complete data sanitization (filter + mask)
 * Applies both layers of protection
 */
export const sanitizeData = (data) => {
  try {
    // Layer 1: Filter out sensitive fields
    const filtered = filterSensitiveFields(data);
    
    // Layer 2: Mask sensitive patterns in remaining data
    const masked = maskSensitivePatterns(filtered);
    
    return masked;
  } catch (error) {
    console.error('Error sanitizing data:', error);
    return {
      sanitizationError: 'Failed to sanitize data',
      originalDataType: typeof data
    };
  }
};

/**
 * Sanitize context data specifically for logging
 */
export const sanitizeLogContext = (context) => {
  if (!context || typeof context !== 'object') return context;
  
  return sanitizeData(context);
};

/**
 * Test data masking functionality
 */
export const testDataMasking = () => {
  const testData = {
    username: 'john_doe',
    email: 'john.doe@example.com',
    password: 'secret123',
    creditCard: '4532-1234-5678-9010',
    token: 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJzdWIiOiIxMjM0NTY3ODkwIiwibmFtZSI6IkpvaG4gRG9lIiwiaWF0IjoxNTE2MjM5MDIyfQ.SflKxwRJSMeKKF2QT4fwpMeJf36POk6yJV_adQssw5c',
    ssn: '123-45-6789',
    phone: '+1-555-123-4567',
    apiKey: 'sk_test_abc123def456ghi789',
    normalData: 'This is normal data',
  };

  console.log('Original:', testData);
  console.log('Sanitized:', sanitizeData(testData));
};

export default {
  filterSensitiveFields,
  maskSensitivePatterns,
  sanitizeData,
  sanitizeLogContext,
  testDataMasking
};
